import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { streamGhost, getGhostHistory } from "@/lib/api";
import { Ghost, Send, Loader2, TriangleAlert } from "lucide-react";

export function GhostInterview({ caseId }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) getGhostHistory(caseId).then(setMessages).catch(() => {});
  }, [open, caseId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, streaming]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "investigator", text: msg }, { role: "ghost", text: "" }]);
    setStreaming(true);
    let acc = "";
    try {
      await streamGhost(caseId, msg, (tok) => { acc += tok; setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "ghost", text: acc }; return c; }); });
    } catch { setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "ghost", text: "…the connection to the reconstruction fades." }; return c; }); }
    finally { setStreaming(false); }
  };

  return (
    <>
      <div className="bg-monk-panel border border-white/10 rounded-md overflow-hidden" data-testid="ghost-launcher">
        <div className="relative p-5 flex items-center gap-4" style={{ background: "linear-gradient(90deg, rgba(176,0,32,0.10), transparent)" }}>
          <div className="w-11 h-11 rounded-md bg-monk-critical/10 border border-monk-critical/30 flex items-center justify-center shrink-0">
            <Ghost className="w-6 h-6 text-monk-critical" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-700 text-sm tracking-tight">Synthetic Killer · Ghost Interview</div>
            <div className="overline mt-1">§15 · Interrogate the offender's psychological replica</div>
          </div>
          <button onClick={() => setOpen(true)} data-testid="ghost-open-btn"
            className="bg-monk-critical/90 text-white text-sm font-medium rounded-sm px-4 py-2.5 hover:bg-monk-critical transition-colors flex items-center gap-2">
            <Ghost className="w-4 h-4" /> Enter Interview
          </button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b0708] border-monk-critical/20 text-white max-w-2xl w-[95vw] p-0 gap-0 overflow-hidden" data-testid="ghost-interview">
          <DialogTitle className="sr-only">Ghost Interview</DialogTitle>
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 bg-black/60">
            <div className="w-9 h-9 rounded-md bg-monk-critical/10 border border-monk-critical/30 flex items-center justify-center"><Ghost className="w-5 h-5 text-monk-critical" /></div>
            <div className="leading-tight flex-1"><div className="font-heading font-700 text-sm tracking-tight text-neutral-100">THE REPLICA</div><div className="overline">Synthetic reconstruction</div></div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-monk-critical/10 border-b border-monk-critical/20">
            <TriangleAlert className="w-3.5 h-3.5 text-monk-critical shrink-0" />
            <p className="text-[11px] text-monk-critical/90">This is a synthetic hypothesis reconstructed from case evidence — not a real person or confession.</p>
          </div>
          <div ref={scrollRef} className="h-[46vh] overflow-y-auto p-4 space-y-4 grid-noise">
            {messages.length === 0 && (
              <p className="hunch-data text-[13px] text-center mt-8">The replica waits in the dark. Ask what the evidence cannot say aloud.</p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "investigator" ? "justify-end" : "justify-start"}`} data-testid={`ghost-msg-${m.role}`}>
                <div className={`max-w-[85%] rounded-md px-3.5 py-2.5 text-[13px] leading-relaxed ${m.role === "investigator" ? "bg-monk-blue text-white" : "bg-black/60 border border-monk-critical/20 text-neutral-300 italic"}`}>
                  {m.text || (streaming && i === messages.length - 1 ? <span className="text-neutral-600 font-mono text-xs">…</span> : "")}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-white/10 bg-black/60 flex items-end gap-2">
            <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask the replica…" data-testid="ghost-input"
              className="flex-1 resize-none bg-black border border-white/15 rounded-md px-3 py-2.5 text-sm focus:border-monk-critical/60 focus:outline-none transition-colors max-h-24" />
            <button onClick={send} disabled={streaming || !input.trim()} data-testid="ghost-send"
              className="w-10 h-10 shrink-0 rounded-md bg-monk-critical text-white flex items-center justify-center hover:bg-monk-critical/90 disabled:opacity-40 transition-colors">
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
